<template>
  <div class="terms-page">
    <!-- ===== 顶部标题区 ===== -->
    <!-- <section class="terms-header">
      <div class="terms-header-bg">
        <div class="terms-orb terms-orb-1"></div>
        <div class="terms-orb terms-orb-2"></div>
      </div>
      <div class="terms-header-content">
        <span class="section-badge">📜 法律文件</span>
        <h1 class="terms-title">Terms of <span class="highlight">Use</span></h1>
        <p class="terms-subtitle">Radiant Infrastructure Ltd</p>
        <p class="terms-desc">
          本协议最后更新于 2026 年 8 月 6 日<br />
          请仔细阅读以下条款和条件
        </p>
      </div>
    </section> -->

    <!-- ===== 主体：左侧锚点 + 右侧内容 ===== -->
    <section class="terms-body">
      <div class="terms-container">
        <!-- 左侧锚点导航 -->
        <aside class="terms-sidebar">
          <div class="sidebar-sticky">
            <div class="sidebar-title">📑 目录</div>
            <nav class="sidebar-nav">
              <a
                v-for="item in toc"
                :key="item.id"
                :href="`#${item.id}`"
                class="sidebar-link"
                :class="{ active: activeSection === item.id }"
                @click.prevent="scrollToSection(item.id)"
              >
                <span class="link-dot"></span>
                {{ item.label }}
              </a>
            </nav>
          </div>
        </aside>

        <!-- 右侧内容 -->
        <main class="terms-content">
          <!-- 1. 协议引言 -->
          <section id="introduction" class="terms-section">
            <h2 class="section-title-text">Introduction</h2>
            <div class="section-content">
              <p>
                This Radiant AI Cloud User Agreement, including all documents
                and terms incorporated by reference herein (collectively, the
                “Agreement”), is entered into by and between
                <strong>Ori Industries 1 Ltd</strong> trading as
                <strong>Radiant</strong>, a United Kingdom company with its
                principal place of business at
                <span class="highlight-text"
                  >Level 25 1 Canada Square, Canary Wharf, London, England, E14
                  5AA, UK</span
                >
                (“Company”) and the organisation you identified when you
                registered to use the Service (“Customer”).
              </p>
              <p>
                This Agreement is effective on the date you registered to use
                the Service (the “Effective Date”). By registering to use the
                Service, you agree to the terms and conditions of this agreement
                as an individual or on behalf of your organisation. You
                represent and warrant that you have the legal authority to bind
                yourself and/or your organisation to this agreement, and that
                you have read and understood this agreement.
              </p>
              <div class="notice-box">
                <span class="notice-icon">⚠️</span>
                <div>
                  <strong>重要提示：</strong>
                  如果您没有这样的授权，或者您或您的组织不同意本协议的条款，您不应接受本协议。
                  Company may make access to the Service(s) and certain features
                  subject to certain requirements or conditions including but
                  not limited to requesting customer information, and meeting
                  specific eligibility requirements.
                </div>
              </div>
            </div>
          </section>

          <!-- 2. Definitions -->
          <section id="definitions" class="terms-section">
            <h2 class="section-title-text">Definitions</h2>
            <div class="section-content">
              <div class="def-grid">
                <div class="def-item">
                  <span class="def-term">"Affiliate"</span>
                  <p class="def-desc">
                    Means any person or entity that controls, is controlled by,
                    or is under common control with another person or entity,
                    where "control" means ownership of fifty percent (50%) or
                    more of the outstanding voting securities.
                  </p>
                </div>
                <div class="def-item">
                  <span class="def-term">"Authorised User"</span>
                  <p class="def-desc">
                    Means a named individual that (a) is an employee,
                    representative, consultant, contractor or agent of Customer
                    or a Customer Affiliate; (b) is authorised to use the
                    Service pursuant to this Agreement; and (c) has been
                    supplied a user identification and password by Customer.
                  </p>
                </div>
                <div class="def-item">
                  <span class="def-term">"Commitment Period"</span>
                  <p class="def-desc">
                    Refers to the duration beginning from the Effective date of
                    Service(s), and ending at the conclusion of the agreed upon
                    term, where Customer has committed to a specified
                    contractual term for a duration specified in the attached
                    Order Form of this agreement.
                  </p>
                </div>
                <div class="def-item">
                  <span class="def-term">"Customer Data"</span>
                  <p class="def-desc">
                    Means any electronic data or materials provided or
                    submitted by Customer or Authorised Users to or through the
                    Service.
                  </p>
                </div>
                <div class="def-item">
                  <span class="def-term">"Documentation"</span>
                  <p class="def-desc">
                    Means the online help materials, including technical
                    specifications, describing the features and functionality
                    of the Service, which are located on the Company's
                    publicly-available website at
                    <a href="#" class="def-link">radiant.co</a>, as updated by
                    the Company from time to time.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <!-- 3. LICENCE AND SUPPORT SERVICES -->
          <section id="licence" class="terms-section">
            <h2 class="section-title-text">Licence and Support Services</h2>
            <div class="section-content">
              <h3>Licence and Access Rights to the Service</h3>
              <p>
                根据本协议的条款和条件，Company 授予 Customer 一项有限的、非排他性的、
                不可转让的权利，允许 Authorised Users 在本协议期限内访问和使用 Service，
                仅用于 Customer 的内部业务运营。
              </p>

              <h3>Restrictions</h3>
              <ul class="terms-list">
                <li>不得对 Service 进行反编译、逆向工程或反汇编</li>
                <li>不得将 Service 用于任何非法目的</li>
                <li>不得将 Service 转售、再许可或转让给第三方</li>
                <li>
                  不得使用 Service 存储或传输任何侵权、诽谤或其他非法材料
                </li>
              </ul>

              <h3>Support Services</h3>
              <p>
                Company 将根据 Documentation 中规定的服务级别向 Customer
                提供技术支持服务。支持服务的范围和级别可能因 Customer
                选择的订阅计划而异。
              </p>
            </div>
          </section>

          <!-- 4. CUSTOMER RESPONSIBILITIES -->
          <section id="customer-responsibilities" class="terms-section">
            <h2 class="section-title-text">
              Customer Responsibilities for Customer Data and Authorised Users
            </h2>
            <div class="section-content">
              <p>
                Customer 对以下事项承担全部责任，并保证所有 Authorised Users 遵守：
              </p>
              <ul class="terms-list">
                <li>
                  <strong>Customer Data:</strong>
                  Customer 对通过 Service 提交的所有 Customer Data 的内容、质量和准确性负责。
                </li>
                <li>
                  <strong>Security:</strong>
                  Customer 负责维护其账号和密码的机密性，并对在其账号下发生的所有活动负责。
                </li>
                <li>
                  <strong>Compliance:</strong>
                  Customer 确保所有 Authorised Users 在使用 Service 时遵守本协议及所有适用法律法规。
                </li>
                <li>
                  <strong>Acceptable Use:</strong>
                  Customer 不得使用 Service 从事任何可能损害 Company 或第三方利益的行为。
                </li>
              </ul>
            </div>
          </section>

          <!-- 5. INTELLECTUAL PROPERTY -->
          <section id="intellectual-property" class="terms-section">
            <h2 class="section-title-text">Intellectual Property Rights and Ownership</h2>
            <div class="section-content">
              <p>
                本协议不将 Company 或其许可方的任何知识产权转让给 Customer。
                Company 保留对 Service、Documentation 以及所有相关技术、算法和软件的所有权利、所有权和利益。
              </p>
              <div class="ip-grid">
                <div class="ip-card">
                  <span class="ip-icon">💻</span>
                  <div>
                    <h4>Company IP</h4>
                    <p>
                      Service、Documentation 以及 Company 提供的所有技术和材料
                    </p>
                  </div>
                </div>
                <div class="ip-card">
                  <span class="ip-icon">📊</span>
                  <div>
                    <h4>Customer Data</h4>
                    <p>Customer 保留对 Customer Data 的所有权利</p>
                  </div>
                </div>
                <div class="ip-card">
                  <span class="ip-icon">🔒</span>
                  <div>
                    <h4>Feedback</h4>
                    <p>
                      Customer 提供的任何反馈、建议或改进意见，Company 可自由使用
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- 6. PAYMENTS -->
          <section id="payments" class="terms-section">
            <h2 class="section-title-text">Payments: Commitments</h2>
            <div class="section-content">
              <p>
                Customer 同意根据 Order Form 中指定的费用标准和付款条款支付所有费用。
                除非本协议另有规定，所有费用均不可退还。
              </p>
              <div class="payment-highlight">
                <div class="payment-item">
                  <span class="payment-label">💳 付款方式</span>
                  <span class="payment-value">信用卡 / 银行转账 / 加密货币</span>
                </div>
                <div class="payment-item">
                  <span class="payment-label">📅 计费周期</span>
                  <span class="payment-value">月度 / 年度（以 Order Form 为准）</span>
                </div>
                <div class="payment-item">
                  <span class="payment-label">⏳ Commitment Period</span>
                  <span class="payment-value">以 Order Form 中约定的期限为准</span>
                </div>
              </div>
            </div>
          </section>

          <!-- 7. TERM & TERMINATION -->
          <section id="termination" class="terms-section">
            <h2 class="section-title-text">Term and Termination</h2>
            <div class="section-content">
              <p>
                本协议自生效日期开始，持续有效直至根据本条款终止。
              </p>
              <ul class="terms-list">
                <li>
                  <strong>Term:</strong>
                  初始期限以 Order Form 中约定的 Commitment Period 为准。
                </li>
                <li>
                  <strong>Renewal:</strong>
                  除非任何一方在期限结束前至少 30 天书面通知不续约，否则本协议将自动续约。
                </li>
                <li>
                  <strong>Termination for Cause:</strong>
                  任何一方在对方重大违约且未在 30 天内补救的情况下，可以终止本协议。
                </li>
                <li>
                  <strong>Effect of Termination:</strong>
                  终止后，Customer 必须立即停止使用 Service，并归还或销毁所有 Confidential Information。
                </li>
              </ul>
            </div>
          </section>

          <!-- 8. REPRESENTATIONS AND WARRANTIES -->
          <section id="warranties" class="terms-section">
            <h2 class="section-title-text">Representations and Warranties</h2>
            <div class="section-content">
              <p>各方声明并保证：</p>
              <ul class="terms-list">
                <li>具有签署和履行本协议的完全法律权力和授权</li>
                <li>本协议对其构成有效且具有约束力的义务</li>
                <li>履行本协议不违反任何适用法律或法规</li>
              </ul>
              <div class="notice-box notice-warning">
                <span class="notice-icon">📢</span>
                <div>
                  <strong>免责声明：</strong>
                  除非本协议另有明确规定，Service 按"现状"提供，Company
                  不作任何明示或暗示的保证，包括但不限于适销性、特定用途适用性和非侵权的保证。
                </div>
              </div>
            </div>
          </section>

          <!-- 9. INDEMNIFICATION -->
          <section id="indemnification" class="terms-section">
            <h2 class="section-title-text">Indemnification</h2>
            <div class="section-content">
              <p>
                <strong>由 Customer 赔偿：</strong>
                Customer 同意赔偿、辩护并使 Company 免受因以下原因引起的任何索赔、损失、损害、责任和费用：
              </p>
              <ul class="terms-list">
                <li>Customer 违反本协议</li>
                <li>Customer 使用 Service 侵犯第三方知识产权</li>
                <li>Customer Data 的内容</li>
              </ul>
              <p>
                <strong>由 Company 赔偿：</strong>
                Company 同意赔偿 Customer 因 Service 侵犯第三方知识产权而导致的索赔。
              </p>
            </div>
          </section>

          <!-- 10. LIMITATION OF LIABILITY -->
          <section id="limitation" class="terms-section">
            <h2 class="section-title-text">Limitation of Liability</h2>
            <div class="section-content">
              <div class="liability-card">
                <div class="liability-item">
                  <span class="liability-icon">🚫</span>
                  <div>
                    <h4>间接损失免责</h4>
                    <p>
                      在任何情况下，任何一方均不对任何间接的、附带的、特殊的或后果性的损害承担责任。
                    </p>
                  </div>
                </div>
                <div class="liability-item">
                  <span class="liability-icon">💰</span>
                  <div>
                    <h4>责任上限</h4>
                    <p>
                      任何一方的总责任不超过 Customer 在引起索赔的事件发生前 12 个月内支付的总费用。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- 11. CONFIDENTIAL INFORMATION -->
          <section id="confidential" class="terms-section">
            <h2 class="section-title-text">Confidential Information</h2>
            <div class="section-content">
              <p>
                "保密信息"指一方（"披露方"）向另一方（"接收方"）披露的、
                标记为保密或根据其性质应被视为保密的任何商业、技术或财务信息。
              </p>
              <ul class="terms-list">
                <li>接收方必须对保密信息保密</li>
                <li>不得将保密信息用于本协议之外的任何目的</li>
                <li>仅向有必要知悉的雇员披露保密信息</li>
                <li>法律要求披露时，需及时通知披露方</li>
              </ul>
            </div>
          </section>

          <!-- 12. GENERAL -->
          <section id="general" class="terms-section">
            <h2 class="section-title-text">General Provisions</h2>
            <div class="section-content">
              <div class="general-grid">
                <div class="general-item">
                  <span class="general-icon">⚖️</span>
                  <div>
                    <h4>Governing Law</h4>
                    <p>本协议受英格兰和威尔士法律管辖并据其解释。</p>
                  </div>
                </div>
                <div class="general-item">
                  <span class="general-icon">🏛️</span>
                  <div>
                    <h4>Jurisdiction</h4>
                    <p>双方同意接受英国法院的专属管辖权。</p>
                  </div>
                </div>
                <div class="general-item">
                  <span class="general-icon">📝</span>
                  <div>
                    <h4>Amendments</h4>
                    <p>Company 可随时修改本协议，修改通知将通过网站或电子邮件发送。</p>
                  </div>
                </div>
                <div class="general-item">
                  <span class="general-icon">✉️</span>
                  <div>
                    <h4>Notices</h4>
                    <p>所有通知应发送至各方指定的注册地址或电子邮件。</p>
                  </div>
                </div>
                <div class="general-item">
                  <span class="general-icon">🔄</span>
                  <div>
                    <h4>Assignment</h4>
                    <p>未经另一方事先书面同意，任何一方不得转让本协议。</p>
                  </div>
                </div>
                <div class="general-item">
                  <span class="general-icon">📄</span>
                  <div>
                    <h4>Entire Agreement</h4>
                    <p>本协议构成双方之间的完整协议，取代所有先前的沟通和协议。</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- 底部页脚 -->
          <div class="terms-footer">
            <p>© 2026 Radiant Infrastructure Ltd. All rights reserved.</p>
            <p class="footer-update">Last Updated: August 6, 2026</p>
          </div>
        </main>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "TermsPage",
  data() {
    return {
      activeSection: "introduction",
      toc: [
        { id: "introduction", label: "Introduction" },
        { id: "definitions", label: "Definitions" },
        { id: "licence", label: "Licence and Support Services" },
        { id: "customer-responsibilities", label: "Customer Responsibilities" },
        { id: "intellectual-property", label: "Intellectual Property" },
        { id: "payments", label: "Payments & Commitments" },
        { id: "termination", label: "Term & Termination" },
        { id: "warranties", label: "Representations & Warranties" },
        { id: "indemnification", label: "Indemnification" },
        { id: "limitation", label: "Limitation of Liability" },
        { id: "confidential", label: "Confidential Information" },
        { id: "general", label: "General Provisions" },
      ],
    };
  },
  methods: {
    scrollToSection(id) {
      this.activeSection = id;
      const element = document.getElementById(id);
      if (element) {
        const offset = 80;
        const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top, behavior: "smooth" });
      }
    },
  },
  mounted() {
    // 滚动监听高亮
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.activeSection = entry.target.id;
          }
        });
      },
      { threshold: 0.3, rootMargin: "-80px 0px 0px 0px" }
    );

    this.toc.forEach((item) => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });
  },
};
</script>

<style scoped>
/* ============================================================
   通用
   ============================================================ */
.terms-page {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  background: #0a0e1a;
  color: #fff;
  overflow-x: clip;
}


.section-badge {
  display: inline-block;
  padding: 4px 16px;
  border-radius: 20px;
  background: rgba(0, 180, 255, 0.08);
  border: 1px solid rgba(0, 180, 255, 0.1);
  font-size: 13px;
  color: #00b4ff;
  margin-bottom: 12px;
}

.highlight {
  background: linear-gradient(135deg, #00b4ff, #7c3aed);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.highlight-text {
  color: #00b4ff;
}

/* ============================================================
   顶部标题区
   ============================================================ */
.terms-header {
  padding: 80px 20px 60px;
  position: relative;
  overflow: hidden;
  text-align: center;
  background: linear-gradient(135deg, #080c18, #0f1a2e);
}

.terms-header-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.terms-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(120px);
  opacity: 0.12;
}

.terms-orb-1 {
  width: 500px;
  height: 500px;
  background: #00b4ff;
  top: -250px;
  left: -100px;
}

.terms-orb-2 {
  width: 400px;
  height: 400px;
  background: #7c3aed;
  bottom: -200px;
  right: -80px;
}

.terms-header-content {
  position: relative;
  z-index: 1;
}

.terms-title {
  font-size: 56px;
  font-weight: 900;
  margin: 8px 0;
}

.terms-subtitle {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0 8px 0;
}

.terms-desc {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.35);
  line-height: 1.8;
  margin: 0;
}

/* ============================================================
   主体：左侧锚点 + 右侧内容
   ============================================================ */
.terms-body {
  padding: 40px 20px 80px;
  background: #0a0e1a;
}

.terms-container {
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  gap: 48px;
  align-items: flex-start;
}

/* ===== 左侧侧边栏 ===== */
.terms-sidebar {
  flex: 0 0 260px;
  position: sticky;
  top: 75px;
  align-self: flex-start;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
}

.terms-sidebar::-webkit-scrollbar {
  display: none; /* Chrome/Safari */
}


.sidebar-sticky {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 24px 16px;
}


.sidebar-title {
  font-size: 14px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 1px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  margin-bottom: 16px;
}

.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sidebar-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  text-decoration: none;
  transition: all 0.25s ease;
  cursor: pointer;
}

.sidebar-link:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.04);
}

.sidebar-link.active {
  color: #00b4ff;
  background: rgba(0, 180, 255, 0.06);
}

.sidebar-link.active .link-dot {
  background: #00b4ff;
  box-shadow: 0 0 12px rgba(0, 180, 255, 0.4);
}

.link-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  flex-shrink: 0;
  transition: all 0.25s ease;
}

.sidebar-link:hover .link-dot {
  background: rgba(255, 255, 255, 0.3);
}

/* ===== 右侧内容 ===== */
.terms-content {
  flex: 1;
  min-width: 0;
}

.terms-section {
  margin-bottom: 48px;
  scroll-margin-top: 80px;
}

.section-title-text {
  font-size: 28px;
  font-weight: 800;
  color: #fff;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid rgba(0, 180, 255, 0.1);
}

.section-content {
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.8;
  font-size: 15px;
}

.section-content p {
  margin: 0 0 16px 0;
}

.section-content h3 {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  margin: 28px 0 12px 0;
}

.section-content h3:first-of-type {
  margin-top: 0;
}

/* ===== 通用列表 ===== */
.terms-list {
  list-style: none;
  padding: 0;
  margin: 0 0 16px 0;
}

.terms-list li {
  position: relative;
  padding: 8px 0 8px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}

.terms-list li::before {
  content: "▸";
  position: absolute;
  left: 0;
  color: #00b4ff;
}

.terms-list li:last-child {
  border-bottom: none;
}

.terms-list li strong {
  color: #fff;
}

/* ===== 通知框 ===== */
.notice-box {
  display: flex;
  gap: 14px;
  padding: 18px 20px;
  border-radius: 12px;
  background: rgba(0, 180, 255, 0.04);
  border: 1px solid rgba(0, 180, 255, 0.08);
  margin: 16px 0;
}

.notice-box.notice-warning {
  background: rgba(255, 215, 0, 0.04);
  border-color: rgba(255, 215, 0, 0.08);
}

.notice-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.notice-box strong {
  color: #fff;
}

/* ===== Definitions 网格 ===== */
.def-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.def-item {
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  transition: all 0.25s ease;
}

.def-item:hover {
  border-color: rgba(0, 180, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
}

.def-term {
  display: inline-block;
  font-size: 16px;
  font-weight: 700;
  color: #00b4ff;
  font-family: "SF Mono", "Courier New", monospace;
  margin-bottom: 6px;
}

.def-desc {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
  line-height: 1.7;
}

.def-link {
  color: #00b4ff;
  text-decoration: none;
}

.def-link:hover {
  text-decoration: underline;
}

/* ===== IP 网格 ===== */
.ip-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 12px;
}

.ip-card {
  display: flex;
  gap: 14px;
  padding: 16px 18px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  transition: all 0.25s ease;
}

.ip-card:hover {
  border-color: rgba(0, 180, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
}

.ip-icon {
  font-size: 28px;
  flex-shrink: 0;
}

.ip-card h4 {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 4px 0;
}

.ip-card p {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  line-height: 1.5;
}

/* ===== Payment 高亮 ===== */
.payment-highlight {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  margin-top: 12px;
}

.payment-item {
  padding: 16px 18px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  text-align: center;
}

.payment-label {
  display: block;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.payment-value {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
}

/* ===== Liability 卡片 ===== */
.liability-card {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.liability-item {
  display: flex;
  gap: 14px;
  padding: 18px 20px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
}

.liability-icon {
  font-size: 28px;
  flex-shrink: 0;
}

.liability-item h4 {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 4px 0;
}

.liability-item p {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  line-height: 1.5;
}

/* ===== General 网格 ===== */
.general-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.general-item {
  display: flex;
  gap: 14px;
  padding: 16px 18px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  transition: all 0.25s ease;
}

.general-item:hover {
  border-color: rgba(0, 180, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
}

.general-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.general-item h4 {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 4px 0;
}

.general-item p {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  line-height: 1.5;
}

/* ===== 底部页脚 ===== */
.terms-footer {
  margin-top: 48px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  text-align: center;
}

.terms-footer p {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.2);
  margin: 4px 0;
}

.terms-footer .footer-update {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.12);
}

/* ============================================================
   响应式
   ============================================================ */
@media (max-width: 1024px) {
  .terms-container {
    flex-direction: column;
  }

  .terms-sidebar {
    flex: unset;
    position: static;
    width: 100%;
  }

  .sidebar-sticky {
    padding: 16px 12px;
  }

  .sidebar-nav {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 4px;
  }

  .sidebar-link {
    padding: 6px 14px;
    font-size: 13px;
  }

  .sidebar-link .link-dot {
    display: none;
  }

  .sidebar-title {
    padding-bottom: 10px;
    margin-bottom: 10px;
  }

  .ip-grid,
  .payment-highlight,
  .liability-card,
  .general-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .terms-title {
    font-size: 36px;
  }

  .terms-header {
    padding: 50px 16px 40px;
  }

  .section-title-text {
    font-size: 22px;
  }

  .terms-body {
    padding: 20px 16px 60px;
  }

  .terms-section {
    margin-bottom: 32px;
  }

  .def-item {
    padding: 12px 16px;
  }

  .payment-highlight {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .terms-title {
    font-size: 28px;
  }

  .section-title-text {
    font-size: 20px;
  }

  .sidebar-link {
    font-size: 12px;
    padding: 4px 10px;
  }

  .ip-card,
  .liability-item,
  .general-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .notice-box {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>